import { MutableRefObject, useEffect, useRef, useState, useCallback } from "react";
import Button from "../../../components/Button/Button";
import { AUTH_STORE_NAME } from "../../../contants/stores";
import { useAppSelector } from "../../../redux";
import { search } from "../../order/actions/order.actions";
import { updateTags } from "../actions/customer.actions";
import AddWalletEntry from "./addWalletEntries";
import { DATABASE } from "../../../config/firebase";
import { formatDate } from "../../../utils/helpers";

export default function CustomerOrderHistory(props: any) {
  const formSubmitButtonRef = useRef() as MutableRefObject<{ click: Function }>;
  const agent = useAppSelector(state => state[AUTH_STORE_NAME].loggedInAgent);
  const [orders, setOrders] = useState<Map<string, string>[]>([]);
  const [showWallet, setShowWallet] = useState(false);
  const [customerTags, setCustomerTags] = useState<string[]>([]);
  
  // Notes functionality
  const [notes, setNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(true);

  const loadCustomerNotes = useCallback(async () => {
    if (!props.id) return;
    
    try {
      setLoadingNotes(true);
      
      const customerDoc = await DATABASE.collection('customers').doc(props.id).get();
      if (customerDoc.exists) {
        const customerData = customerDoc.data();
        if (customerData?.notes && Array.isArray(customerData.notes)) {
          const notesData = customerData.notes.map((note: any, index: number) => ({
            id: `note_${index}`,
            ...note
          }));
          setNotes(notesData);
        } else {
          setNotes([]);
        }
      } else {
        setNotes([]);
      }
      
    } catch (error) {
      console.log('Error loading notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  }, [props.id]);

  useEffect(() => {
    let ordersList = (!props?.orders || !props?.orders?.length) ? [] : [...props?.orders];
    ordersList = ordersList?.length ? ordersList?.reverse() : [];
    setOrders(ordersList);

    let tagsList = (!props?.tags || !props?.tags?.length) ? [] : [...props?.tags];
    setCustomerTags(tagsList);
    
    // Load customer notes
    loadCustomerNotes();
  }, [props.orders, props.tags, loadCustomerNotes]);

  const addNote = async () => {
    if (!newNote.trim() || !props.id) return;
    
    try {
      const noteData = {
        content: newNote.trim(),
        createdBy: agent.name || agent.email || 'Unknown',
        createdAt: new Date(),
        agentId: agent.id || agent.uid
      };
      
      // Get current customer data
      const customerRef = DATABASE.collection('customers').doc(props.id);
      const customerDoc = await customerRef.get();
      
      if (customerDoc.exists) {
        const currentData = customerDoc.data();
        const existingNotes = currentData?.notes || [];
        const updatedNotes = [...existingNotes, noteData];
        
        // Use the same approach as the save function
        await customerRef.update({
          notes: updatedNotes,
          lastNoteAdded: new Date()
        });
        
        setNewNote('');
        loadCustomerNotes(); // Reload notes
      }
      
    } catch (error) {
      console.log('Error adding note:', error);
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!props.id || !noteId) return;
    
    try {
      const customerRef = DATABASE.collection('customers').doc(props.id);
      const customerDoc = await customerRef.get();
      
      if (customerDoc.exists) {
        const currentData = customerDoc.data();
        const existingNotes = currentData?.notes || [];
        const noteIndex = parseInt(noteId.replace('note_', ''));
        const updatedNotes = existingNotes.filter((_: any, index: number) => index !== noteIndex);
        
        await customerRef.update({
          notes: updatedNotes
        });
        
        loadCustomerNotes(); // Reload notes
      }
      
    } catch (error) {
      console.log('Error deleting note:', error);
    }
  };

  const toggleSearchDialog = async (orderNo: string) => {
    await search('ORDER', orderNo);
  }

  const removeTag = (val: string) => {
    const tags = customerTags?.filter(
      (tag: string) => tag !== val
    );
    return tags;
  }

  const handleChange = (tag: string) => {
    let tags: any = [...customerTags];
    const index = tags.findIndex((x: string) => x === tag);
    if (index > -1) {
      tags = removeTag(tag)
    } else {
      tags.push(tag);
    }
    setCustomerTags(tags);
  }

  const saveChanges = async () => {
    await updateTags(props.id, customerTags);
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center ">
          <svg aria-hidden="true" className="h-4 mx-2" viewBox="0 0 40 38" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M39.0728 0L21.9092 12.6999L25.1009 5.21543L39.0728 0Z" fill="#E17726" /><path d="M0.966797 0.0151367L14.9013 5.21656L17.932 12.7992L0.966797 0.0151367Z" fill="#E27625" /><path d="M32.1656 27.0093L39.7516 27.1537L37.1004 36.1603L27.8438 33.6116L32.1656 27.0093Z" fill="#E27625" /><path d="M7.83409 27.0093L12.1399 33.6116L2.89876 36.1604L0.263672 27.1537L7.83409 27.0093Z" fill="#E27625" /><path d="M17.5203 10.8677L17.8304 20.8807L8.55371 20.4587L11.1924 16.4778L11.2258 16.4394L17.5203 10.8677Z" fill="#E27625" /><path d="M22.3831 10.7559L28.7737 16.4397L28.8067 16.4778L31.4455 20.4586L22.1709 20.8806L22.3831 10.7559Z" fill="#E27625" /><path d="M12.4115 27.0381L17.4768 30.9848L11.5928 33.8257L12.4115 27.0381Z" fill="#E27625" /><path d="M27.5893 27.0376L28.391 33.8258L22.5234 30.9847L27.5893 27.0376Z" fill="#E27625" /><path d="M22.6523 30.6128L28.6066 33.4959L23.0679 36.1282L23.1255 34.3884L22.6523 30.6128Z" fill="#D5BFB2" /><path d="M17.3458 30.6143L16.8913 34.3601L16.9286 36.1263L11.377 33.4961L17.3458 30.6143Z" fill="#D5BFB2" /><path d="M15.6263 22.1875L17.1822 25.4575L11.8848 23.9057L15.6263 22.1875Z" fill="#233447" /><path d="M24.3739 22.1875L28.133 23.9053L22.8184 25.4567L24.3739 22.1875Z" fill="#233447" /><path d="M12.8169 27.0049L11.9606 34.0423L7.37109 27.1587L12.8169 27.0049Z" fill="#CC6228" /><path d="M27.1836 27.0049L32.6296 27.1587L28.0228 34.0425L27.1836 27.0049Z" fill="#CC6228" /><path d="M31.5799 20.0605L27.6165 24.0998L24.5608 22.7034L23.0978 25.779L22.1387 20.4901L31.5799 20.0605Z" fill="#CC6228" /><path d="M8.41797 20.0605L17.8608 20.4902L16.9017 25.779L15.4384 22.7038L12.3988 24.0999L8.41797 20.0605Z" fill="#CC6228" /><path d="M8.15039 19.2314L12.6345 23.7816L12.7899 28.2736L8.15039 19.2314Z" fill="#E27525" /><path d="M31.8538 19.2236L27.2061 28.2819L27.381 23.7819L31.8538 19.2236Z" fill="#E27525" /><path d="M17.6412 19.5088L17.8217 20.6447L18.2676 23.4745L17.9809 32.166L16.6254 25.1841L16.625 25.1119L17.6412 19.5088Z" fill="#E27525" /><path d="M22.3562 19.4932L23.3751 25.1119L23.3747 25.1841L22.0158 32.1835L21.962 30.4328L21.75 23.4231L22.3562 19.4932Z" fill="#E27525" /><path d="M27.7797 23.6011L27.628 27.5039L22.8977 31.1894L21.9414 30.5138L23.0133 24.9926L27.7797 23.6011Z" fill="#F5841F" /><path d="M12.2373 23.6011L16.9873 24.9926L18.0591 30.5137L17.1029 31.1893L12.3723 27.5035L12.2373 23.6011Z" fill="#F5841F" /><path d="M10.4717 32.6338L16.5236 35.5013L16.4979 34.2768L17.0043 33.8323H22.994L23.5187 34.2753L23.48 35.4989L29.4935 32.641L26.5673 35.0591L23.0289 37.4894H16.9558L13.4197 35.0492L10.4717 32.6338Z" fill="#C0AC9D" /><path d="M22.2191 30.231L23.0748 30.8354L23.5763 34.8361L22.8506 34.2234H17.1513L16.4395 34.8485L16.9244 30.8357L17.7804 30.231H22.2191Z" fill="#161616" /><path d="M37.9395 0.351562L39.9998 6.53242L38.7131 12.7819L39.6293 13.4887L38.3895 14.4346L39.3213 15.1542L38.0875 16.2779L38.8449 16.8264L36.8347 19.1742L28.5894 16.7735L28.5179 16.7352L22.5762 11.723L37.9395 0.351562Z" fill="#763E1A" /><path d="M2.06031 0.351562L17.4237 11.723L11.4819 16.7352L11.4105 16.7735L3.16512 19.1742L1.15488 16.8264L1.91176 16.2783L0.678517 15.1542L1.60852 14.4354L0.350209 13.4868L1.30098 12.7795L0 6.53265L2.06031 0.351562Z" fill="#763E1A" /><path d="M28.1861 16.2485L36.9226 18.7921L39.7609 27.5398L32.2728 27.5398L27.1133 27.6049L30.8655 20.2912L28.1861 16.2485Z" fill="#F5841F" /><path d="M11.8139 16.2485L9.13399 20.2912L12.8867 27.6049L7.72971 27.5398H0.254883L3.07728 18.7922L11.8139 16.2485Z" fill="#F5841F" /><path d="M25.5283 5.17383L23.0847 11.7736L22.5661 20.6894L22.3677 23.4839L22.352 30.6225H17.6471L17.6318 23.4973L17.4327 20.6869L16.9139 11.7736L14.4707 5.17383H25.5283Z" fill="#F5841F" /></svg>
          WalletBalance: {props.walletBalance ?? 0} KD</div>
        {agent.isSuperAdmin ?
          <button onClick={() => { setShowWallet(!showWallet) }}
            type="button" data-modal-toggle="crypto-modal" className="text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center dark:focus:ring-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700">
            <svg aria-hidden="true" className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>
            Add to wallet
          </button>
          : <></>}
      </div>

      {showWallet ? <AddWalletEntry id={props.id} setShowWallet={setShowWallet} /> : null}

      {/* Customer Notes Section */}
      <div className="my-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Customer Notes
        </h3>
        
        {/* Add Note Form */}
        <div className="mb-4">
          <div className="flex gap-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note about this customer..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              rows={3}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addNote();
                }
              }}
            />
            <button
              onClick={addNote}
              disabled={!newNote.trim()}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Note
            </button>
          </div>
          {/* <p className="text-xs text-gray-500 mt-1">Press Ctrl+Enter to add note quickly</p> */}
        </div>

        {/* Notes List */}
        <div className="space-y-3">
          {loadingNotes ? (
            <div className="flex justify-center py-4">
              <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              No notes yet. Add the first note above.
            </div>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{note.createdBy}</span>
                    <span className="text-xs text-gray-500">
                      {note.createdAt?.toDate ? formatDate(note.createdAt.toDate()) : formatDate(note.createdAt)}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete note"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="my-3 p-2 border border-gray-200 rounded-lg">
        <form onSubmit={(e: any) => {
          e.preventDefault();
          formSubmitButtonRef?.current?.click();
        }}>
          <div className="grid grid-cols-2 gap-1">
            <div className="grid grid-rows">
              {props?.positiveTags?.map((tag: string) => (
                <div key={tag} className="flex justify-between items-center m-1 pl-3 py-1 border border-green-300 rounded-lg bg-green-200 text-base text-green-700 font-medium">
                  <div className="flex-1">
                    <label className="space-x-3"
                      key="positive"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        handleChange(tag)
                      }}>
                      <input type="checkbox" defaultChecked={props?.tags?.includes(tag)} value="positive" className="form-tick  bg-white bg-check h-4 w-4 border border-gray-300 rounded-md checked:bg-blue-500 checked:border-transparent focus:outline-none hover:cursor-pointer" />
                      <span className="text-xs font-normal">{tag}</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-rows">
              {props?.negativeTags?.map((tag: string) => (
                <div key={tag} className="flex justify-between items-center m-1 pl-3 px-2 py-1 border border-red-300 rounded-lg bg-red-200 text-base text-red-700 font-medium">
                  <div className="flex-1">
                    <label className="space-x-3"
                      key="negative"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e: any) => {
                        handleChange(tag)
                      }}>
                      <input type="checkbox" defaultChecked={props?.tags?.includes(tag)} value="negative" className="form-tick bg-white bg-check h-4 w-4 border border-gray-300 rounded-md checked:bg-blue-500 checked:border-transparent focus:outline-none hover:cursor-pointer" />
                      <span className="text-xs font-normal">{tag}</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end my-3 mx-1">
            <Button
              ref={formSubmitButtonRef}
              type="submit"
              notify={false}
              id="editCart"
              cb={saveChanges}
              text="Save"
              loadingText="Saving..."
              successMsg="Changes saved successfully"
            />
          </div>
        </form>
      </div>

      {orders?.length > 0 && <div className="my-3 p-2 border border-gray-200 rounded-lg">
        <ul className="grid grid-cols-2 gap-3">
          {orders?.map((order: any) => (
            <li key={order.orderNo}>
              <div className="flex items-center p-3 text-sm font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow">
                <span key={order.orderNo} className="flex-1 ml-3 whitespace-nowrap">{order.orderNo}</span>
                <span onClick={async () => { await toggleSearchDialog(order.orderNo) }} className="cursor-pointer inline-flex items-center justify-center px-2 py-0.5 ml-3 text-xs font-medium text-gray-500 bg-gray-200 rounded">Details</span>
              </div>
            </li>
          ))}
        </ul>
      </div>}
    </>
  )
}